import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import type { SupplyAppMutationPayload, SupplyInventoryApp, SupplyInventoryAppEdit } from './model';

const requiredText = (message: Parameters<typeof dashboardT>[0]) =>
  z.string().trim().min(1, dashboardT(message));

export const supplyInventoryAppFormSchema = z.object({
  name: requiredText('supplyApps.validation.name'),
  bundleId: requiredText('supplyApps.validation.bundleId'),
  appStoreUrl: requiredText('supplyApps.validation.appStoreUrl'),
  domain: z.string(),
  platformId: requiredText('supplyApps.validation.platform'),
  appStoreId: requiredText('supplyApps.validation.appStoreId'),
  numericId: z.string(),
  language: z.string(),
  publisherIds: z.array(z.string()),
  adsTxtLocation: z.string(),
  iabCategoryIds: z.array(z.string()).max(1),
  description: z.string(),
});

export type SupplyInventoryAppFormValue = z.infer<typeof supplyInventoryAppFormSchema>;

export function supplyInventoryAppToFormValue(
  app: SupplyInventoryApp,
): SupplyInventoryAppFormValue {
  return {
    name: app.name,
    bundleId: app.bundleId ?? '',
    appStoreUrl: app.appStoreUrl ?? '',
    domain: app.domain ?? '',
    platformId: app.platform?.id ?? '',
    appStoreId: app.appStoreId ?? '',
    numericId: app.numericId ?? '',
    language: app.language ?? '',
    publisherIds: app.publisherIds,
    adsTxtLocation: app.adsTxtLocation ?? '',
    iabCategoryIds: app.iabCategories.slice(0, 1).map((category) => category.id),
    description: app.description ?? '',
  };
}

export function serializeSupplyInventoryAppUpdate(
  app: SupplyInventoryAppEdit,
  value: SupplyInventoryAppFormValue,
): SupplyAppMutationPayload {
  const source = app.sourceDto;
  const sourcePlatform = source.platform;

  return {
    ...source,
    id: app.id,
    name: value.name.trim(),
    bundleId: value.bundleId.trim(),
    appStoreUrl: value.appStoreUrl.trim(),
    domain: preserveNullableEmptyString(source.domain, value.domain),
    platform:
      isRecord(sourcePlatform) && sourcePlatform.id === value.platformId
        ? sourcePlatform
        : { id: value.platformId, name: app.platform?.name ?? value.platformId },
    appStoreId: value.appStoreId.trim(),
    numericId: value.numericId.trim(),
    language: preserveNullableEmptyString(source.language, value.language),
    publisherIds: value.publisherIds,
    adsTxtLocation: preserveNullableEmptyString(source.adsTxtLocation, value.adsTxtLocation),
    iabCategories: value.iabCategoryIds,
    description: preserveNullableEmptyString(source.description, value.description),
  };
}

function preserveNullableEmptyString(sourceValue: unknown, value: string): string | null {
  const normalized = value.trim();
  return normalized === '' && sourceValue === null ? null : normalized;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
